import React, { useState, useEffect } from 'react';
import api from '../services/bookingApi';
import '../Components/PackManagement.css'; // Reuse existing CSS

const ServiceManager = () => {
  const [services, setServices] = useState([]);
  const [categories, setCategories] = useState([]);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    duration: 30, // par défaut 30min
    price: 0,
    category: '',
    location: ''
  });
  const [categoryFormData, setCategoryFormData] = useState({
    name: '',
    description: ''
  });
  const [editingService, setEditingService] = useState(null);
  const [message, setMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [activeTab, setActiveTab] = useState('list');

  // Get user info
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const isAdmin = user.role === 'admin';
  const userLocation = localStorage.getItem('userLocation');

  const [availableLocations, setAvailableLocations] = useState([]);

  useEffect(() => {
    fetchData();
    if (isAdmin) {
      fetchLocations();
    }
  }, [userLocation, isAdmin]);

  const fetchData = async () => {
    setLoading(true);
    await Promise.all([fetchServices(), fetchCategories()]);
    setLoading(false);
  };

  const [loading, setLoading] = useState(true);

  const fetchLocations = async () => {
    try {
      const data = await api.getLocations();
      setAvailableLocations(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Erreur chargement locations:", error);
    }
  };

  const fetchServices = async () => {
    try {
      const filters = userLocation ? { location: userLocation } : {};
      const data = await api.getServices(filters);
      setServices(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Erreur chargement services:', error);
      setMessage('❌ Impossible de charger les services.');
    }
  };

  const fetchCategories = async () => {
    try {
      const data = await api.getCategories();
      setCategories(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Erreur chargement catégories:', error);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name === 'duration' || name === 'price' ? parseFloat(value) : value
    }));
  };

  const handleCategoryChange = (e) => {
    const { name, value } = e.target;
    setCategoryFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage('');
    setIsSubmitting(true);

    try {
      const locationToSave = isAdmin ? formData.location : (userLocation || formData.location);
      const dataToSend = { ...formData, location: locationToSave };

      if (editingService) {
        await api.updateService(editingService._id, dataToSend);
        setMessage('✅ Service mis à jour !');
      } else {
        await api.createService(dataToSend);
        setMessage('✅ Service créé !');
      }

      setFormData({
        name: '',
        description: '',
        duration: 30,
        price: 0,
        category: '',
        location: isAdmin ? '' : (userLocation || '')
      });
      setEditingService(null);
      fetchServices();
      setActiveTab('list');
    } catch (error) {
      console.error('Erreur:', error);
      setMessage(`❌ Erreur: ${error.message || 'Problème lors de l\'enregistrement'}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCategorySubmit = async (e) => {
    e.preventDefault();
    setMessage('');
    setIsSubmitting(true);
    try {
      await api.createCategory(categoryFormData);
      setMessage('✅ Catégorie ajoutée !');
      setCategoryFormData({ name: '', description: '' });
      fetchCategories();
    } catch (error) {
      console.error('Erreur catégorie:', error);
      setMessage('❌ Erreur lors de l\'ajout de la catégorie.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEdit = (service) => {
    setFormData({
      name: service.name,
      description: service.description || '',
      duration: service.duration,
      price: service.price,
      category: service.category || '',
      location: service.location || ''
    });
    setEditingService(service);
    setActiveTab('create');
  };

  const handleDelete = async (id) => {
    if (window.confirm('Voulez-vous vraiment supprimer ce service ?')) {
      try {
        await api.deleteService(id);
        setMessage('✅ Service supprimé !');
        fetchServices();
      } catch (error) {
        console.error('Erreur:', error);
        setMessage('❌ Erreur lors de la suppression.');
      }
    }
  };

  const cancelEdit = () => {
    setFormData({
      name: '',
      description: '',
      duration: 30,
      price: 0,
      category: '',
      location: isAdmin ? '' : (userLocation || '')
    });
    setEditingService(null);
    setActiveTab('list');
  };

  return (
    <div className="pack-management-container">
      <div className="pack-management-card">
        <h2 className="pack-management-title">
          <span className="pack-icon">⚙️</span>
          Administration des Services {userLocation && `(${userLocation})`}
        </h2>

        {message && (
          <div className={`pack-message ${message.includes('❌') ? 'pack-error' : 'pack-success'}`}>
            {message}
          </div>
        )}

        <div className="pack-tabs">
          <button
            className={`pack-tab ${activeTab === 'list' ? 'active' : ''}`}
            onClick={() => setActiveTab('list')}
          >
            Liste des Services
          </button>
          <button
            className={`pack-tab ${activeTab === 'create' ? 'active' : ''}`}
            onClick={() => {
              setEditingService(null);
              setActiveTab('create');
            }}
          >
            {editingService ? 'Modifier Service' : 'Nouveau Service'}
          </button>
          <button
            className={`pack-tab ${activeTab === 'categories' ? 'active' : ''}`}
            onClick={() => setActiveTab('categories')}
          >
            Gérer les Catégories
          </button>
        </div>

        {activeTab === 'create' && (
          <form className="pack-form" onSubmit={handleSubmit}>
            <div className="pack-form-grid">
              <div className="pack-form-group">
                <label className="pack-form-label">Nom du Service *</label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  className="pack-form-input"
                  required
                />
              </div>

              <div className="pack-form-group">
                <label className="pack-form-label">Catégorie</label>
                <select
                  name="category"
                  value={formData.category}
                  onChange={handleChange}
                  className="pack-form-select"
                  required
                >
                  <option value="">-- Choisir une catégorie --</option>
                  {categories.map(cat => (
                    <option key={cat._id} value={cat.name}>{cat.name}</option>
                  ))}
                </select>
                <small style={{ cursor: 'pointer', color: '#007bff' }} onClick={() => setActiveTab('categories')}>
                  + Ajouter une nouvelle catégorie
                </small>
              </div>

              <div className="pack-form-group">
                <label className="pack-form-label">Prix (DT) *</label>
                <input
                  type="number"
                  name="price"
                  value={formData.price}
                  onChange={handleChange}
                  className="pack-form-input"
                  min="0"
                  required
                />
              </div>

              <div className="pack-form-group">
                <label className="pack-form-label">Durée (minutes) *</label>
                <input
                  type="number"
                  name="duration"
                  value={formData.duration}
                  onChange={handleChange}
                  className="pack-form-input"
                  step="15"
                  min="15"
                  required
                />
              </div>

              {isAdmin && (
                <div className="pack-form-group">
                  <label className="pack-form-label">Localisation (Admin)</label>
                  <select
                    name="location"
                    value={formData.location}
                    onChange={handleChange}
                    className="pack-form-select"
                  >
                    <option value="">-- Sélectionner une localisation --</option>
                    {availableLocations.map(loc => (
                      <option key={loc._id} value={loc.name}>{loc.name}</option>
                    ))}
                  </select>
                </div>
              )}

              <div className="pack-form-group full-width">
                <label className="pack-form-label">Description</label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  className="pack-form-textarea"
                  rows="3"
                />
              </div>
            </div>

            <div className="pack-form-actions">
              <button type="button" onClick={cancelEdit} className="pack-cancel-btn">Annuler</button>
              <button type="submit" disabled={isSubmitting} className="pack-submit-btn">
                {isSubmitting ? 'Enregistrement...' : (editingService ? 'Mettre à jour' : 'Créer')}
              </button>
            </div>
          </form>
        )}

        {activeTab === 'categories' && (
          <div className="categories-management">
            <form className="pack-form" onSubmit={handleCategorySubmit} style={{ marginBottom: '30px', borderBottom: '1px solid #eee', paddingBottom: '20px' }}>
              <h3>➕ Ajouter une nouvelle catégorie</h3>
              <div className="pack-form-grid">
                <div className="pack-form-group">
                  <label className="pack-form-label">Nom de la catégorie *</label>
                  <input
                    type="text"
                    name="name"
                    value={categoryFormData.name}
                    onChange={handleCategoryChange}
                    className="pack-form-input"
                    required
                  />
                </div>
                <div className="pack-form-group">
                  <label className="pack-form-label">Description</label>
                  <input
                    type="text"
                    name="description"
                    value={categoryFormData.description}
                    onChange={handleCategoryChange}
                    className="pack-form-input"
                  />
                </div>
              </div>
              <button type="submit" disabled={isSubmitting} className="pack-submit-btn" style={{ marginTop: '10px' }}>
                Ajouter la catégorie
              </button>
            </form>

            <h3>📋 Catégories existantes</h3>
            <div className="pack-grid">
              {categories.map(cat => (
                <div key={cat._id} className="pack-card" style={{ padding: '10px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <strong>{cat.name}</strong>
                    <div style={{ fontSize: '12px', color: '#666' }}>{cat.description}</div>
                  </div>
                </div>
              ))}
              {categories.length === 0 && <div className="pack-empty">Aucune catégorie créée.</div>}
            </div>
          </div>
        )}

        {activeTab === 'list' && (
          <div className="pack-list">
            {services.length === 0 ? (
              <div className="pack-empty">Aucun service disponible.</div>
            ) : (
              <div className="pack-grid">
                {services.map(service => (
                  <div key={service._id} className="pack-card">
                    <div className="pack-card-header">
                      <h3 className="pack-card-title">{service.name}</h3>
                      <span className="pack-card-price">{service.price} DT</span>
                    </div>
                    <div className="pack-card-details">
                      <div className="pack-card-detail">⏱️ {service.duration} min</div>
                      <div className="pack-card-detail">🏷️ {service.category}</div>
                      {service.location && <div className="pack-card-detail">📍 {service.location}</div>}
                    </div>
                    {service.description && (
                      <div className="pack-card-description">{service.description}</div>
                    )}
                    <div className="pack-card-actions">
                      <button onClick={() => handleEdit(service)} className="pack-edit-btn">Modifier</button>
                      <button onClick={() => handleDelete(service._id)} className="pack-delete-btn">Supprimer</button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ServiceManager;
